<script setup>
  import { computed, watch, onUnmounted } from "vue";
  import { storeToRefs } from "pinia";
  import { useSlideTemplatesStore } from "@stores/slideTemplates";
  import { useReportsV2Store } from "@stores/survey/reportsV2/reportsV2";
  import { useSurveyStore } from "@stores/survey/survey";
  import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
  import { Loader2, Presentation } from "lucide-vue-next";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import { LoadingState } from "@/components/episto-ui/loading-state";

  const props = defineProps({
    open: Boolean,
  });

  const emit = defineEmits(["update:open", "select"]);

  const store = useSlideTemplatesStore();
  const { templates, loading } = storeToRefs(store);

  const reportsStore = useReportsV2Store();
  const { currentReport } = storeToRefs(reportsStore);

  const { token: surveyToken } = storeToRefs(useSurveyStore());

  const themeToken = computed(() => currentReport.value?.master_report?.token);
  const reportThemeId = computed(() => currentReport.value?.report_theme_id ?? null);

  // Theme slide previews are generated asynchronously (debounced job): right
  // after a theme styling change they may not exist yet. While the dialog is
  // open we show a "Generating preview…" state and poll until they land.
  const POLL_INTERVAL = 4000;
  const MAX_POLLS = 10;
  let pollTimer = null;

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function loadTemplates() {
    return store.fetchTemplates(themeToken.value, reportThemeId.value, surveyToken.value);
  }

  const allPreviewsReady = () =>
    templates.value.length > 0 && templates.value.every((t) => t.preview_image_url);

  watch(
    () => props.open,
    async (isOpen) => {
      stopPolling();
      if (!isOpen || !themeToken.value) return;

      await loadTemplates();
      if (!reportThemeId.value || allPreviewsReady()) return;

      let attempts = 0;
      pollTimer = setInterval(async () => {
        attempts += 1;
        await loadTemplates();
        if (allPreviewsReady() || attempts >= MAX_POLLS) stopPolling();
      }, POLL_INTERVAL);
    },
  );

  onUnmounted(stopPolling);

  function selectTemplate(templateId) {
    emit("select", templateId);
    emit("update:open", false);
  }
</script>

<template>
  <Dialog
    :open="open"
    @update:open="$emit('update:open', $event)">
    <DialogContent class="max-w-4xl">
      <DialogHeader>
        <DialogTitle>Select a slide template</DialogTitle>
      </DialogHeader>

      <div class="py-4">
        <!-- Loading -->
        <LoadingState
          v-if="loading"
          class="py-8" />

        <!-- Empty state -->
        <EmptyState
          v-else-if="templates.length === 0"
          :icon="Presentation"
          title="No templates available"
          description="Import a PowerPoint presentation first" />

        <!-- Template grid -->
        <div
          v-else
          class="grid grid-cols-3 gap-4 max-h-[36rem] overflow-y-auto p-1">
          <div
            v-for="template in templates"
            :key="template.id"
            class="border rounded-lg p-3 cursor-pointer hover:border-primary/50 transition-all"
            @click="selectTemplate(template.id)">
            <!-- Thumbnail -->
            <div class="aspect-video bg-muted rounded overflow-hidden mb-3 flex flex-col items-center justify-center gap-1.5">
              <img
                v-if="template.preview_image_url"
                :src="template.preview_image_url"
                :alt="template.name"
                class="w-full h-full object-cover" />
              <template v-else-if="reportThemeId">
                <Loader2 class="w-5 h-5 text-muted-foreground animate-spin" />
                <span class="text-2xs text-muted-foreground">Generating preview…</span>
              </template>
              <Presentation
                v-else
                class="w-8 h-8 text-muted-foreground" />
            </div>

            <!-- Info -->
            <span class="text-xs text-foreground font-medium truncate block">{{ template.name }}</span>
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
